import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { confirmRefrenceCondition } from "../../network/cipAPI";

import createNotification, {
  SUCCESS_NOTIFICATION,
  ERROR_NOTIFICATION,
} from "../../components/Notifications";
import CircularLoader from "../../common/styles/components/circularLoader";

import {
  UpdateSetWithKey,
  unitConverter,
  compareRelativeFields,
} from "../../utils/Utils";
import {
  ALLOWED_MIN_CONVERSION_UNITS,
  COMPARE_KEYS,
} from "../../constants/global.constants";
import {
  REFERENCE_CONDITION_OPTIONS,
  REFERENCE_CONDITION_RELATIVE_COMPARISION,
} from "./columns.config";

import { WPRangedInput } from "../../packages/WPInput/WPRangedInput";
import WPTertiaryButton from "../../packages/WPTertiaryButton";
import { SaveIcon } from "../../packages/SVGs";
import IconWithTooltip from "../../common/styles/components/headings/IconWithTooltip";

import "./index.scss";

const ReferenceCondition = ({
  fileId,
  mappedReference,
  tempReferenceValues,
  selectedColumnRadioColumn,
  selectedTrain,
  setMandatoryComponentsStatus,
}) => {
  const { t } = useTranslation();

  const referenceColumns = useMemo(
    () => REFERENCE_CONDITION_OPTIONS(t, selectedColumnRadioColumn),
    [t, selectedColumnRadioColumn],
  );

  const { flow, pressure, feed_quality, permeate_quality, temperature } =
    useSelector((state) => state);

  const unitSettings = useMemo(
    () => ({
      flow,
      pressure,
      feed_quality,
      permeate_quality,
      temperature: `\u00B0${temperature}`,
    }),
    [flow, pressure, feed_quality, permeate_quality, temperature],
  );

  const [selectedReference, setSelectedReference] = useState({});
  const [changedSelectedReference, setChangedSelectedReference] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isAnimatingTempRef, setIsAnimatingTempRef] = useState(false);
  const [errors, setErrors] = useState({
    feed_flowrate: null,
    concentrate_flowrate: null,
    permeate_flowrate: null,
    feed_pressure: null,
    permeate_pressure: null,
    feed_temp: null,
    permeate_quality: null,
    feed_quality: null,
    pressure_drop: null,
    concentrate_pressure: null,
  });

  const validateAllFields = (referenceValues, isChanged = true) => {
    // reset reference, errors and changes
    let newSelectedReference = {};
    let newErrors = {};
    setChangedSelectedReference({});

    // First pass: Update all values and validate ranges
    Object.keys(referenceValues).forEach((key) => {
      const fieldConfig = referenceColumns.find((field) => field.key === key);
      if (fieldConfig) {
        const ranges = fieldConfig.ranges?.hard;
        const convertedRanges = getConvertedRanges(ranges, fieldConfig.unit);
        const value = referenceValues[key];
        const { min, max } = convertedRanges.hard;

        newSelectedReference[key] = value;

        // Range validation only
        const isRangeValid = validateFieldRange(value, min, max);

        newErrors[key] = isRangeValid ? null : true;
      }
    });

    // Second pass: Relative comparison validation with complete data
    Object.keys(referenceValues).forEach((key) => {
      newErrors = applyRelativeComparison(key, newSelectedReference, newErrors);
    });

    // Update state once with all accumulated changes
    setErrors(newErrors);
    setSelectedReference(newSelectedReference);

    if (isChanged) {
      setChangedSelectedReference((prevSet) => {
        let updated = { ...prevSet };
        Object.keys(referenceValues).forEach((key) => {
          updated = UpdateSetWithKey(updated, key, referenceValues[key]);
        });
        return updated;
      });
    }
  };

  useEffect(() => {
    if (tempReferenceValues) {
      setIsAnimatingTempRef(true);
      validateAllFields(tempReferenceValues);
      // Reset animation after 2 seconds
      setTimeout(() => {
        setIsAnimatingTempRef(false);
      }, 2000);
    }
  }, [tempReferenceValues]);

  useEffect(() => {
    if (mappedReference) {
      const formattedReference = {};
      for (const key in mappedReference) {
        const val = mappedReference[key];
        if (typeof val === "number") {
          formattedReference[key] =
            val === 0 ? val.toString() : Number(val.toFixed(2));
        } else {
          formattedReference[key] = val;
        }
      }
      validateAllFields(formattedReference, false);
    } else {
      setSelectedReference({});
    }
  }, [mappedReference, selectedColumnRadioColumn]);

  useEffect(() => {
    if (!fileId) {
      setSelectedReference({});
      setChangedSelectedReference({});
    }
  }, [fileId]);

  const getConvertedRanges = (ranges, unit) => {
    let { min, max } = ranges;
    const isMinConversionAllowed = ALLOWED_MIN_CONVERSION_UNITS.includes(unit);
    const convertedMin = isMinConversionAllowed
      ? unitConverter(min, unitSettings[unit])
      : min;
    const convertedMax = unitConverter(max, unitSettings[unit]);

    let convertedRanges = { hard: { min: convertedMin, max: convertedMax } };
    return convertedRanges;
  };

  const compareList = useMemo(
    () => REFERENCE_CONDITION_RELATIVE_COMPARISION(),
    [],
  );

  // Validate range for a single field
  const validateFieldRange = (value, min, max) => {
    return !(
      value === "" ||
      parseFloat(value) < min ||
      parseFloat(value) > max
    );
  };

  // Apply relative comparison validation and clear related field errors
  const applyRelativeComparison = (key, refValues, currentErrors) => {
    let newErrors = { ...currentErrors };
    const compareResult = compareRelativeFields(key, refValues, compareList);

    if (compareResult === null) {
      // Only clear if not a range error
      if (!newErrors[key]) {
        newErrors[key] = null;
      }

      // Clear errors on compared keys as well
      const comparedKeyList = compareList
        .filter(
          ([mainKey, compareKey]) => key === mainKey || key === compareKey,
        )
        .map(([mainKey, compareKey]) =>
          key === mainKey ? compareKey : mainKey,
        );

      comparedKeyList.forEach((comparedKey) => {
        newErrors[comparedKey] = null;
      });
    } else {
      newErrors[key] = compareResult;
    }

    return newErrors;
  };

  const onInputValChange = (value, key, range) => {
    const { min, max } = range;
    let newErrors = { ...errors };
    let newRefValues = { ...selectedReference, [key]: value };

    // Range validation
    const isRangeValid = validateFieldRange(value, min, max);
    newErrors[key] = isRangeValid ? null : true;

    // Relative comparison validation
    newErrors = applyRelativeComparison(key, newRefValues, newErrors);

    setErrors(newErrors);
    setSelectedReference(newRefValues);
    setChangedSelectedReference((prevSet) =>
      UpdateSetWithKey(prevSet, key, value),
    );
  };

  const getRelativeErrorMsg = (fieldKey) => {
    const error = errors[fieldKey];
    if (error && error !== true) {
      const compareText =
        error[0] === COMPARE_KEYS.GREATER
          ? t(`ValueStrings.mustBeGreaterThan`)
          : t(`ValueStrings.mustBeLessThan`);

      const relativeErrorMsg = `${t(
        `referenceConditions.${fieldKey}`,
      )} ${compareText} ${t(`referenceConditions.${error[1]}`)}`;
      return relativeErrorMsg;
    }
    return null;
  };

  const checkAllRequiredFieldsFilled = () => {
    const {
      feed_flowrate,
      concentrate_flowrate,
      permeate_flowrate,
      feed_pressure,
      permeate_pressure,
      feed_temp,
      permeate_quality,
      feed_quality,
      concentrate_pressure,
      pressure_drop,
    } = selectedReference;

    const hasAllInputs =
      (feed_flowrate || concentrate_flowrate) &&
      permeate_flowrate &&
      feed_pressure &&
      permeate_pressure &&
      feed_temp &&
      permeate_quality &&
      feed_quality &&
      (concentrate_pressure || pressure_drop);

    return Boolean(hasAllInputs);
  };

  const checkChangedFields = () => {
    return Object.keys(changedSelectedReference).length > 0;
  };

  const checkValidationErrors = () => {
    return Object.values(errors).some((error) => error !== null);
  };

  const getConfirmButtonState = () => {
    let disableConfirmButton = true;

    const hasAllInputs = checkAllRequiredFieldsFilled();
    const hasChangedFields = checkChangedFields();
    const hasErrors = checkValidationErrors();

    if (hasAllInputs && hasChangedFields && !hasErrors) {
      disableConfirmButton = false;
    }

    return disableConfirmButton;
  };

  const checkNDPValidation = () => {
    // NDP (Net Driving Pressure) validation
    try {
      const feedPressure = unitConverter(
        parseFloat(selectedReference.feed_pressure),
        unitSettings.pressure,
        true,
      );
      const concentratePressure = unitConverter(
        parseFloat(selectedReference.concentrate_pressure) ||
          parseFloat(selectedReference.feed_pressure) -
            (unitConverter(
              parseFloat(selectedReference.pressure_drop),
              unitSettings.pressure,
              true,
            ) || 0),
        unitSettings.pressure,
        true,
      );
      const permeatePressure = unitConverter(
        parseFloat(selectedReference.permeate_pressure),
        unitSettings.pressure,
        true,
      );
      const feedQuality = parseFloat(
        unitConverter(
          selectedReference.feed_quality,
          unitSettings.conductivity,
          true,
        ),
      ); // µS/cm
      const permeateQuality = parseFloat(
        unitConverter(
          selectedReference.permeate_quality,
          unitSettings.conductivity,
          true,
        ),
      ); // µS/cm
      const feedTemp = parseFloat(
        unitConverter(
          selectedReference.feed_temp,
          unitSettings.temperature,
          true,
        ),
      ); // °C

      // Left side: 0.5×(feed_pressure+concentrate_pressure)−permeate_pressure
      const leftSide =
        0.5 * (feedPressure + concentratePressure) - permeatePressure;

      // Right side osmotic pressure calculations
      // Feed osmotic pressure: 0.0385×feed_quality×(feed_temp+273.15)/(1000−feed_quality/1000)/14.5
      const feedOsmotic =
        (0.0385 * feedQuality * (feedTemp + 273.15)) /
        (1000 - feedQuality / 1000) /
        14.5;

      // Permeate osmotic pressure: 0.0385×permeate_quality×(feed_temp+273.15)/(1000−permeate_quality/1000)/14.5
      const permeateOsmotic =
        (0.0385 * permeateQuality * (feedTemp + 273.15)) /
        (1000 - permeateQuality / 1000) /
        14.5;

      // Right side: feed_osmotic - permeate_osmotic
      const rightSide = feedOsmotic - permeateOsmotic;

      // NDP condition: leftSide > rightSide (must be > 0)
      const ndpResult = leftSide - rightSide;

      if (ndpResult < 0) {
        return t("referenceConditions.NDPValidationFailed");
      } else {
        return true;
      }
    } catch (error) {
      if (error.message.includes("Division by zero")) {
        return `
            ${t("referenceConditions.NDPValidationError")}: ${t(
              "referenceConditions.divisionByZeroError",
            )}
          `;
      } else {
        return `${t("referenceConditions.NDPValidationError")}: ${
          error.message
        }`;
      }
    }
  };

  const confirmRefrenceConditions = async () => {
    setIsSaving(true);

    try {
      const NDPValidationResult = checkNDPValidation();

      if (NDPValidationResult !== true) {
        throw new Error(NDPValidationResult);
      }

      let payload = null;
      if (mappedReference) payload = changedSelectedReference;
      else payload = selectedReference;
      const response = await confirmRefrenceCondition(
        fileId,
        payload,
        selectedTrain,
      );

      if (response && response.detail === undefined) {
        setChangedSelectedReference({});
        createNotification(
          SUCCESS_NOTIFICATION,
          t("referenceConditions.referenceAdded"),
          "",
        );
      } else {
        const errorMsg = response.detail?.error || response.detail;
        throw new Error(errorMsg);
      }
    } catch (err) {
      createNotification(
        ERROR_NOTIFICATION,
        err.message || t("ValueStrings.somethingWentWrong"),
        "",
      );
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    const hasAllInputs = checkAllRequiredFieldsFilled();
    const hasChangedFields = checkChangedFields();
    const hasErrors = checkValidationErrors();

    const referenceConditionsStatus = {
      fulfilled: hasAllInputs,
      changed: hasChangedFields,
      error: hasErrors,
    };

    setMandatoryComponentsStatus((prevStatus) => ({
      ...prevStatus,
      referenceConditions: referenceConditionsStatus,
    }));
  }, [selectedReference, changedSelectedReference, errors]);

  return (
    <div className="ro-card-wrapper position-relative">
      {isSaving && (
        <div className="circular-loader-overlay">
          <CircularLoader />
        </div>
      )}
      <div className="ro-card-header">
        <div className="info-section">
          <div className="teal-heading">{t("referenceConditions.heading")}</div>
          <IconWithTooltip
            label={t("helpInformation.referenceConditionsHelp")}
          />
        </div>

        <WPTertiaryButton
          variant="outlined"
          label={t("ValueStrings.confirm")}
          icon={<SaveIcon width="16" height="16" />}
          iconPlacement="before"
          size="small"
          onClick={confirmRefrenceConditions}
          disabled={getConfirmButtonState()}
          className={`${
            isAnimatingTempRef && !getConfirmButtonState()
              ? "temp-reference-animating"
              : ""
          }`}
        />
      </div>
      {/* <div className="ro-component-description">
        {t("referenceConditions.description")}
      </div> */}
      <div className="reference-condition-container ">
        {referenceColumns.map((field) => {
          const convertedRanges = getConvertedRanges(
            field.ranges?.hard,
            field.unit,
          );

          return (
            <div className="reference-item" key={field.key}>
              <WPRangedInput
                key={field.key}
                value={selectedReference[field.key] || ""}
                name={field.key}
                label={field.name}
                suffix={unitSettings[field.unit]}
                ranges={convertedRanges}
                onChange={(e) =>
                  onInputValChange(
                    e.target.value,
                    field.key,
                    convertedRanges.hard,
                  )
                }
                required={true}
                requiredMsg={t("ValueStrings.requiredField")}
                externalError={getRelativeErrorMsg(field.key)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
export default ReferenceCondition;
